/**
 * A project's worktrees by what they need: active (a session is on it), waiting to be landed
 * (ahead of main, clean, nobody on it), or uncommitted with nobody on it. Pure, so it is tested.
 */
import type { Checkout, Session } from '@orgus/protocol';

export interface WorktreeStates {
  /** A live session works in it. */
  active: number;
  /** Commits ahead of main not landed yet, nothing uncommitted, no session: waiting to be landed. */
  toLand: number;
  /** Uncommitted changes and no session: pending work nobody is on. */
  uncommitted: number;
}

/** What a project's worktrees are doing, by the facts the checkouts and sessions carry. Clean, merged ones are not counted. */
export function worktreeStates(checkouts: readonly Pick<Checkout, 'id' | 'ahead' | 'unlanded' | 'dirty'>[], sessions: readonly Pick<Session, 'checkoutId' | 'status'>[]): WorktreeStates {
  const live = new Set(sessions.filter((s) => s.checkoutId && (s.status === 'working' || s.status === 'waiting')).map((s) => s.checkoutId));
  const out: WorktreeStates = { active: 0, toLand: 0, uncommitted: 0 };
  for (const c of checkouts) {
    if (live.has(c.id)) out.active += 1;
    else if (c.dirty > 0) out.uncommitted += 1;
    // A queue that lands copies (cherry-pick, rebase) leaves a landed branch ahead by hash.
    else if ((c.unlanded ?? c.ahead) > 0) out.toLand += 1;
  }
  return out;
}

/** One quiet line: "3 active, 2 to land, 9 uncommitted", leaving out the zeros. */
export function worktreeLine(w: WorktreeStates): string {
  return [w.active && `${w.active} active`, w.toLand && `${w.toLand} to land`, w.uncommitted && `${w.uncommitted} uncommitted`].filter(Boolean).join(', ');
}

